// The demo project page: the header card with the analysis picker, the call
// to action as a dark card (a decision is one button with the other ways out
// in its menu; what the user must provide reopens the intake), and the report
// under them.

import { FC, ReactNode, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Result, StatusDotColor, Tooltip, message } from "../../../../design-system";
import InfinitySpinner from "../../../../common/components/InfinitySpinner";
import { ActionCard } from "../components/ActionCard";
import { AnalysisPicker } from "../components/AnalysisPicker";
import { AsksCard } from "../components/AsksCard";
import { Field, HeaderCard, OwnerName } from "../components/HeaderCard";
import { dealOf, projectOf, vendorOf } from "../model/derived";
import { DEMO_TODAY, ME } from "../model/fixtures";
import { longDate, money, moneyRange, relativeDays, shortDate } from "../model/format";
import { decide, deliver, useUsageDemoState } from "../model/store";
import type { Decision, ReportKey } from "../model/types";
import { usageIntakePath } from "../paths";
import { reportHtml } from "../reports";
import classes from "./UsageProjectView.module.less";

const STATUS: Record<Decision["kind"] | "opportunity", { label: string; color: StatusDotColor }> = {
    opportunity: { label: "Opportunity", color: "orange" },
    requested: { label: "Asks open", color: "blue" },
    submitted: { label: "In analysis", color: "blue" },
    delivered: { label: "Playbook delivered", color: "green" },
    revision: { label: "Playbook update requested", color: "orange" },
    proposal: { label: "Round 2 in preparation", color: "blue" },
    signed: { label: "Signed", color: "green" },
    cancelled: { label: "Cancelled", color: "grey" },
    renew: { label: "Renewing as is", color: "grey" },
    snoozed: { label: "Snoozed", color: "grey" },
    dismissed: { label: "Dismissed", color: "grey" },
};

/** The eyebrow of a card InfinityLoop is working behind: the spinner, small, then the words. */
const Working: FC<{ children?: ReactNode }> = ({ children = "In analysis" }) => (
    <span className={classes.working}>
        <InfinitySpinner size={14} style={{ display: "inline-flex" }} />
        {children}
    </span>
);

/** A quiet button that opens the browser's calendar; the date picked is the answer. */
const DateButton: FC<{ label: string; initial: string; onPick: (date: string) => void }> = ({
    label,
    initial,
    onPick,
}) => {
    const input = useRef<HTMLInputElement>(null);
    const openCalendar = () => {
        const el = input.current;
        if (!el) return;
        if ("showPicker" in el) (el as HTMLInputElement & { showPicker: () => void }).showPicker();
        else el.focus();
    };
    return (
        <span className={classes.dateAnchor}>
            <Button type="text" className={classes.secondary} onClick={openCalendar}>
                {label}
            </Button>
            <input
                ref={input}
                className={classes.hiddenDate}
                type="date"
                tabIndex={-1}
                aria-hidden
                defaultValue={initial}
                min={DEMO_TODAY}
                onChange={(e) => e.target.value && onPick(e.target.value)}
            />
        </span>
    );
};

/** A way out of a decision other than the main action: a click, or a date to pick. */
type Secondary =
    | { label: string; onClick: () => void }
    | { label: string; initial: string; onPick: (date: string) => void };

/** A decision: the other ways out as quiet text buttons at the left, the main action in blue at the right. */
const DecisionButton: FC<{
    label: string;
    onClick: () => void;
    menu: Secondary[];
}> = ({ label, onClick, menu }) => (
    <>
        {menu.map((item) =>
            "onPick" in item ? (
                <DateButton key={item.label} {...item} />
            ) : (
                <Button
                    key={item.label}
                    type="text"
                    className={classes.secondary}
                    onClick={item.onClick}
                >
                    {item.label}
                </Button>
            )
        )}
        <Button type="primary" onClick={onClick}>
            {label}
        </Button>
    </>
);

export const UsageProjectView: FC = () => {
    const { projectId = "" } = useParams();
    const navigate = useNavigate();
    const state = useUsageDemoState();
    const project = projectOf(projectId);
    // The report shown once there are two: the latest, unless the user picked another.
    const [shownReport, setShownReport] = useState<ReportKey | undefined>(undefined);
    if (!project) return <Result status="404" title="No such project in the demo" />;

    const deal = dealOf(project);
    const vendor = vendorOf(project);
    const { deliverable, decisions } = project;
    const decision = state.decisions[project.id];
    const status = STATUS[decision?.kind ?? "opportunity"];
    const history = state.history[project.id] ?? [];
    /** When the playbook landed, whether that is the current step or a past one. */
    const dateOf = (kind: "delivered"): string | undefined => {
        for (const d of [decision, ...[...history].reverse()]) if (d?.kind === kind) return d.on;
        return undefined;
    };
    const deliveredOn = dateOf("delivered");
    const hasPlaybook = Boolean(deliveredOn && deliverable.report);

    // The stages, each with its report; the latest carries the project's current status.
    const analyses = [
        {
            key: project.report,
            label: "Opportunity",
            date: hasPlaybook ? longDate(project.reportedOn) : status.label,
            color: hasPlaybook ? ("grey" as const) : status.color,
            latest: !hasPlaybook,
        },
        ...(hasPlaybook
            ? [
                  {
                      key: deliverable.report!,
                      label: "Negotiation",
                      date: status.label,
                      color: status.color,
                      latest: true,
                  },
              ]
            : []),
    ];
    const report = analyses.some((a) => a.key === shownReport)
        ? shownReport!
        : analyses[analyses.length - 1].key;

    // ---- The decisions: a few actions, one click ----
    const opportunityCard = (
        <ActionCard
            eyebrow="Action for you"
            title="Review the opportunity report and decide"
            lines={[
                `InfinityLoop raised this opportunity on ${longDate(project.reportedOn)} from the usage data. ${project.notification.reason}`,
            ]}
            actions={
                <DecisionButton
                    label={decisions.request.label}
                    onClick={() => navigate(usageIntakePath(project.id))}
                    menu={[
                        {
                            label: decisions.decline.label,
                            onClick: () => {
                                if (decisions.decline.kind === "dismiss")
                                    navigate(usageIntakePath(project.id, "dismiss"));
                                else {
                                    decide(project.id, { kind: "renew", on: DEMO_TODAY });
                                    message.success("Recorded: the agreement renews as is");
                                }
                            },
                        },
                        {
                            label: decisions.snooze.label,
                            initial: decisions.snooze.until,
                            onPick: (until) => {
                                decide(project.id, { kind: "snoozed", until });
                                message.success(`Reminder set for ${longDate(until)}`);
                            },
                        },
                    ]}
                />
            }
        />
    );
    // The playbook is in: the user takes it to the vendor and comes back with their answer,
    // asks for a change to it first, or closes the deal either way.
    const deliveredCard = (
        <ActionCard
            eyebrow={`Action for you · ${longDate(deliveredOn ?? DEMO_TODAY)}`}
            title={`Review ${deliverable.name} and take the opening position to ${vendor.name}`}
            actions={
                <DecisionButton
                    label="Share the updated proposal"
                    onClick={() => navigate(usageIntakePath(project.id, "proposal"))}
                    menu={[
                        {
                            label: "Request an update",
                            onClick: () => navigate(usageIntakePath(project.id, "revision")),
                        },
                        {
                            label: "Cancel the deal",
                            onClick: () => navigate(usageIntakePath(project.id, "cancel")),
                        },
                    ]}
                />
            }
        />
    );

    let card: ReactNode;
    switch (decision?.kind) {
        case undefined:
            card = opportunityCard;
            break;
        case "requested":
            card = <AsksCard project={project} state={state} />;
            break;
        case "submitted":
            card = (
                <ActionCard
                    eyebrow={<Working />}
                    title={`We are working on ${deliverable.name}`}
                    lines={[
                        `Submitted for analysis ${longDate(decision.on)}. You get ${deliverable.name} within 3 business days, well before the ${shortDate(deal.term.noticeBy)} notice.`,
                    ]}
                    actions={
                        <>
                            {deliverable.report && (
                                <Tooltip
                                    title="Demo: the playbook lands as if the analysts had finished"
                                    mouseEnterDelay={0.3}
                                >
                                    <Button
                                        ghost
                                        size="small"
                                        className={classes.demoBtn}
                                        onClick={() =>
                                            deliver(
                                                project.id,
                                                deliverable.deliveredOn ?? DEMO_TODAY
                                            )
                                        }
                                    >
                                        Simulate the delivery
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    }
                />
            );
            break;
        case "delivered":
            card = deliveredCard;
            break;
        case "revision":
            card = (
                <ActionCard
                    eyebrow={<Working />}
                    title={`InfinityLoop revises ${deliverable.name}`}
                    lines={[`Update requested ${longDate(decision.on)}: ${decision.comment}`]}
                    actions={
                        <Tooltip title="Demo: the revised playbook lands" mouseEnterDelay={0.3}>
                            <Button
                                ghost
                                size="small"
                                className={classes.demoBtn}
                                onClick={() =>
                                    deliver(project.id, deliverable.revisedOn ?? DEMO_TODAY)
                                }
                            >
                                Simulate the delivery
                            </Button>
                        </Tooltip>
                    }
                />
            );
            break;
        case "proposal":
            card = (
                <ActionCard
                    eyebrow={<Working />}
                    title={`InfinityLoop prepares round 2 from ${vendor.name}'s answer`}
                    lines={[`Vendor proposal updated ${longDate(decision.on)}: ${decision.note}`]}
                />
            );
            break;
        case "signed":
            card = (
                <ActionCard
                    eyebrow={<Working>Savings report in preparation</Working>}
                    title="We are working on the savings report"
                    lines={[
                        "You get the savings report, from the signed terms, within 3 business days.",
                    ]}
                />
            );
            break;
        case "cancelled":
            card = (
                <ActionCard
                    eyebrow="Decision"
                    title={`Cancelled ${longDate(decision.on)}`}
                    lines={decision.reason ? [`Reason given: ${decision.reason}`] : []}
                />
            );
            break;
        case "renew":
            card = (
                <ActionCard
                    eyebrow="Decision"
                    title={`Decided ${longDate(decision.on)} to let the agreement renew as is.`}
                />
            );
            break;
        case "snoozed":
            card = (
                <ActionCard
                    eyebrow="Decision"
                    title={`Reminder set for ${longDate(decision.until)}.`}
                />
            );
            break;
        case "dismissed":
            card = (
                <ActionCard
                    eyebrow="Decision"
                    title={`Dismissed ${longDate(decision.on)}: the consumption growth is planned`}
                    lines={decision.reason ? [`Reason given: ${decision.reason}`] : []}
                />
            );
            break;
    }

    return (
        <div className={classes.page}>
            <HeaderCard
                vendor={vendor}
                logoFor={deal.name}
                name={project.name}
                detail={vendor.name}
            >
                <Field label="Stage">
                    <AnalysisPicker items={analyses} value={report} onChange={setShownReport} />
                </Field>
                <Field label={deal.type === "consumption" ? "Annual commitment" : "Annual TCV"}>
                    {money(deal.annualCost)}
                </Field>
                <Field label="Utilization">
                    <Tooltip title={project.utilization.detail} mouseEnterDelay={0.3}>
                        <span
                            className={project.utilization.value > 1 ? classes.over : classes.under}
                        >
                            {Math.round(project.utilization.value * 100)}%
                        </span>
                    </Tooltip>
                </Field>
                <Field label="At stake">
                    <span className={classes.atStake}>{moneyRange(project.atStake)}</span>
                    <span className={classes.muted}> a year</span>
                </Field>
                <Field label="Owner">
                    <OwnerName name={ME} />
                </Field>
                <Field label="Notice by">
                    <Tooltip title={relativeDays(deal.term.noticeBy)} mouseEnterDelay={0.3}>
                        <span>{longDate(deal.term.noticeBy)}</span>
                    </Tooltip>
                </Field>
            </HeaderCard>
            {card}
            <iframe className={classes.frame} srcDoc={reportHtml(report)} title="Report" />
        </div>
    );
};
